import { useRender } from '@base-ui/react/use-render';
import type { MouseEvent, ReactElement } from 'react';
import { useToggleGroup } from '../containers/toggle-group.tsx';
import { cn } from '../util/classes.ts';

const baseClasses = cn(
    'appearance-none select-none inline-flex shrink-0 items-center justify-center gap-cozy whitespace-nowrap cursor-pointer',
    'h-interactive min-w-interactive rounded-default px-relaxed',
    'state-disabled:pointer-events-none state-disabled:opacity-disabled',
    'transition duration-state ease-state',
    '[input[aria-label]]:after:content-[attr(aria-label)]',
    'active:translate-px',
    'text-content border border-line',
    'hover:bg-content/(--opacity-wash)',
    'state-checked-within:text-brand state-checked-within:bg-brand/(--opacity-wash)',
    'hover:state-checked-within:bg-brand/(--opacity-wash-hover)',
    '[&_input]:sr-only [&_input]:appearance-none',
    // joined children: square inner corners and overlap adjacent borders
    'joined-trailing:rounded-l-none joined-trailing:-ml-px',
    'joined-leading:rounded-r-none',
    // raise active element so its border isn't covered by neighbors
    'joined:focus-visible:relative joined:focus-visible:z-10',
    'joined:state-checked-within:relative joined:state-checked-within:z-10',
);

export type ToggleButtonProps = useRender.ComponentProps<'button'> & {
    checked?: boolean;
    optionValue?: string;
};

export function ToggleButton({
    checked: checkedFromProps,
    className,
    optionValue,
    render,
    ...props
}: ToggleButtonProps): ReactElement {
    const [groupState, setGroupState] = useToggleGroup();
    const checkedFromGroup = groupState === undefined ? undefined : groupState === optionValue;
    const checked = checkedFromProps ?? checkedFromGroup;

    return useRender({
        defaultTagName: 'button',
        render,
        props: {
            'data-state': checked === true ? 'checked' : checked === false ? 'unchecked' : undefined,
            'data-value': optionValue,
            ...props,
            onClick: (e: MouseEvent<HTMLButtonElement>) => {
                props.onClick?.(e);
                if (!e.defaultPrevented) {
                    setGroupState(optionValue);
                }
            },
            className: cn(baseClasses, className),
        },
    });
}
